import { ElementType, FunctionComponent, memo, useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSearchParams } from 'react-router-dom';
import { InAppMessageFeedCard, PostedIn } from '../../../components';
import {
  HorizontalStoryFeedData,
  NewsFeedInAppMessageData,
  NewsFeedType,
  StoryCardItemData,
} from '../../../services';
import { useAppSelector, useNewsFeed } from '../../hooks';
import { isAdminLayout } from '../../utils';
import { FeedCardProps } from '../FeedCard';
import { HorizontalStoryFeedProps } from '../HorizontalFeed';

import classes from './NewsFeedCommon.module.scss';

interface NewsFeedCommonProps {
  loader: JSX.Element;
  loadMore: () => void;
  HorizontalStoryFeedComponent: ElementType<HorizontalStoryFeedProps>;
  CardComponent: ElementType<FeedCardProps>;
}

export const NewsFeedCommon: FunctionComponent<NewsFeedCommonProps> = memo(
  ({ loader, loadMore, HorizontalStoryFeedComponent, CardComponent }) => {
    const [searchParams] = useSearchParams();

    const { pinnedStorySection } = useNewsFeed();

    const { newsFeed, hasNextPage } = useAppSelector(({ newsFeed }) => newsFeed);

    const isSubTabActive = useMemo(() => Boolean(searchParams.get('storyId')), [searchParams]);

    const hidePostedIn = useMemo(
      () => isSubTabActive || (isAdminLayout() && pinnedStorySection),
      [isSubTabActive, pinnedStorySection]
    );

    return (
      <InfiniteScroll
        next={loadMore}
        hasMore={hasNextPage}
        loader={loader}
        dataLength={newsFeed.length}
        className={classes['feed']}
        style={{ overflowX: 'hidden' }} //needed because of horizontal feed card actions
      >
        <div className={classes['feed__cards']}>
          {newsFeed.map((newsFeedItem, index, newsFeedArray) => {
            switch (newsFeedItem.type) {
              case NewsFeedType.HORIZONTAL_STORY_FEED: {
                const data = newsFeedItem.data as HorizontalStoryFeedData;
                const { items, pageInfo, storyId } = data;

                if (!items.length) {
                  return null;
                }

                return (
                  <HorizontalStoryFeedComponent
                    key={`horizontal-story-feed-${storyId}`}
                    story={items[0].story}
                    cards={items}
                    pageInfo={pageInfo}
                    zIndex={newsFeed.length - index} //needed for card actions
                  />
                );
              }
              case NewsFeedType.STORY_CARD_ITEM: {
                const card = (newsFeedItem.data as StoryCardItemData).item;
                const prevItem = newsFeedArray[index - 1];

                return (
                  <div key={`story-card-item-${card.id}`}>
                    {!hidePostedIn &&
                      (prevItem?.type !== NewsFeedType.STORY_CARD_ITEM ||
                        (prevItem?.type === NewsFeedType.STORY_CARD_ITEM &&
                          (prevItem.data as StoryCardItemData).item.story.id !==
                            card.story.id)) && <PostedIn story={card.story} />}
                    <CardComponent card={card} />
                  </div>
                );
              }
              case NewsFeedType.IN_APP_MESSAGE: {
                if (isAdminLayout()) {
                  return null;
                }

                const message = newsFeedItem.data as NewsFeedInAppMessageData;

                return (
                  <InAppMessageFeedCard key={`in-app-message-${message.id}`} message={message} />
                );
              }
              default:
                return null;
            }
          })}
        </div>
      </InfiniteScroll>
    );
  }
);
